import { cn } from 'noddi-ui-common';
import { forwardRef, useId } from 'react';
import { NoddiTooltip } from '../Overlay/NoddiTooltip';
import { NoddiCopyButton } from './NoddiCopyButton';

const NoddiInput = forwardRef<
  HTMLInputElement,
  React.ComponentProps<'input'> & {
    label?: string;
    tooltip?: string;
    startAdornment?: React.ReactNode;
    endAdornment?: React.ReactNode;
    wrapperClassName?: string;
    labelClassName?: string;
    copyToClipboard?: boolean;
    copySuccessMessage?: string;
  }
>(
  (
    {
      className,
      type,
      label,
      tooltip,
      startAdornment,
      endAdornment,
      wrapperClassName,
      labelClassName,
      copyToClipboard,
      copySuccessMessage,
      disabled,
      ...props
    },
    ref
  ) => {
    const generatedId = useId();
    const controlId = props.id ?? props.name ?? generatedId;
    const labelIdentifier = `${controlId}-label`;

    return (
      <div className={cn('flex flex-col gap-0.5', wrapperClassName)}>
        {label && (
          <div className='flex items-center gap-1'>
            <label id={labelIdentifier} htmlFor={controlId} className={cn('text-sm', labelClassName)}>
              {label}
            </label>
            {tooltip && <NoddiTooltip content={tooltip} label={`More information about ${label}`} />}
          </div>
        )}
        <div className='flex items-center gap-1'>
          <div
            className={cn(
              'flex h-9 items-center rounded-md border border-input bg-white shadow-xs transition-colors focus-within:ring-1 focus-within:ring-ring',
              disabled && 'cursor-not-allowed opacity-50',
              copyToClipboard ? 'min-w-0 flex-1' : 'w-full',
              className
            )}
          >
            {startAdornment && <div className='ml-3 flex shrink-0'>{startAdornment}</div>}
            <input
              id={controlId}
              type={type}
              disabled={disabled}
              className={cn(
                'flex w-full bg-transparent px-3 py-1 text-base transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-hidden disabled:cursor-not-allowed disabled:opacity-50 md:text-sm',
                !startAdornment && 'rounded-l-md',
                !endAdornment && 'rounded-r-md'
              )}
              ref={ref}
              {...props}
            />
            {endAdornment && <div className='mr-3 flex shrink-0'>{endAdornment}</div>}
          </div>
          {copyToClipboard && (
            <NoddiCopyButton
              textToCopy={props.value?.toString() ?? ''}
              successMessage={copySuccessMessage}
              variant='ghost'
              size='large'
              className='shrink-0'
            />
          )}
        </div>
      </div>
    );
  }
);

NoddiInput.displayName = 'NoddiInput';

export { NoddiInput };
